import User from "../../../classes/User";
import { Dark, LocalStorage } from "quasar";
import { i18n } from "../../../boot/i18n";
import { auth, db } from "../../../boot/firebase";
import {
  applyActionCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";
import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  endAt,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAt,
  where,
} from "firebase/firestore";
import { formatPlainText, timestampToDate } from "../../../util/misc";
import { isArray, isFunction } from "lodash";

export const TOGGLE_DARK = ({ commit }, auto = false) => {
  let mode;
  if (auto) {
    mode = "auto";
  } else {
    mode = !Dark.mode;
  }
  Dark.set(mode);
  LocalStorage.set("dark", mode);
  commit("SET_DARK", mode);
};

export const TOGGLE_ABSOLUTE_TIME = ({ commit, state }) => {
  LocalStorage.set("absoluteTime", !state.absoluteTime);
  commit("SET_ABSOLUTE_TIME", !state.absoluteTime);
};

export const TOGGLE_EXPAND_NAV = ({ commit, state }) => {
  LocalStorage.set("expandNav", !state.expandNav);
  commit("SET_EXPAND_NAV", !state.expandNav);
};

export const MARK_CHANGELOG_SEEN = ({ commit }, version) => {
  const seen = version || process.env.APP_VERSION || null;
  LocalStorage.set("changelogLastSeenVersion", seen);
  commit("SET_CHANGELOG_LAST_SEEN_VERSION", seen);
};

// Capture the version currently seen as the comparison anchor for the What's
// New dialog. Storing the baseline in Vuex keeps the "new vs previous" split
// stable even if the dialog component remounts (e.g. DialogRouterView re-keys
// when the user object updates), which would otherwise re-read an
// already-advanced watermark and hide all "new" releases. Only capture once
// per session so re-opening the dialog (manually or via remount) doesn't
// overwrite the anchor with the now-current version.
export const CAPTURE_CHANGELOG_BASELINE = ({ commit, state }) => {
  if (!state.changelogBaselineVersion) {
    commit("SET_CHANGELOG_BASELINE_VERSION", state.changelogLastSeenVersion);
  }
};

export const TOGGLE_MESSAGES = ({ commit, state }) => {
  LocalStorage.set("showMessages", !state.showMessages);
  commit("SHOW_MESSAGES", !state.showMessages);
};

export const SHOW_MESSAGES = ({ commit }, value) => {
  LocalStorage.set("showMessages", value);
  commit("SHOW_MESSAGES", value);
};

export const SET_MESSAGES_PROJECT = ({ commit }, id) => {
  if (id) {
    LocalStorage.set("messagesProjectID", id);
  } else {
    LocalStorage.remove("messagesProjectID");
  }
  commit("SET_MESSAGES_PROJECT", id || null);
};

// Opens the messages panel on a project, scrolled to one of its messages (a
// message link's route, a task made from a message). `reply`: "reply" or
// "reply-all" also starts a reply to it, as its own menu would.
export const OPEN_MESSAGE = (
  { commit, dispatch },
  { projectID, messageID, reply }
) => {
  dispatch("SHOW_MESSAGES", true);
  dispatch("SET_MESSAGES_PROJECT", projectID);
  commit("SCROLL_TO_MESSAGE", messageID);
  if (reply) {
    commit("START_REPLY", { messageID, all: reply === "reply-all" });
  }
};

export const INIT = ({ commit, dispatch, getters, state }) => {
  return new Promise((resolve, reject) => {
    if (state.user) {
      resolve(state.user);
    }
    auth.onAuthStateChanged(
      async (user) => {
        if (user) {
          // Logged in
          const claims = (await user.getIdTokenResult()).claims;
          user = new User(
            user.uid,
            (await getDoc(doc(db, User.getPath(user.uid)))).data()
          );
          commit("SET_CURRENT_USER", { user, claims });
          dispatch("LISTEN_CURRENT_USER");
          dispatch("INIT_PUSH_NOTIFICATIONS");
          dispatch("LISTEN_NOTIFICATION_COUNTS");
          dispatch("LISTEN_NOTIFICATIONS");
          if (user.isEmployee) {
            dispatch("LISTEN_MESSAGES", {
              where: ["createdBy", "==", user.id],
              listenerPath: "messages-sent",
            });
            dispatch("LISTEN_MESSAGES", {
              where: ["to", "array-contains", user.id],
              listenerPath: "messages-received",
            });
            dispatch("LISTEN_ACCOUNTS", {});
            dispatch("LISTEN_COMPANIES", {});
            dispatch("LISTEN_CONTACTS", {});
            dispatch("LISTEN_USERS", {});
            dispatch("LISTEN_VEHICLES", {});
            dispatch("LISTEN_PROJECTS", {});
            dispatch("LISTEN_WAREHOUSE_MAPS", {});
            // Out-of-office events, for OOO awareness when assigning tasks
            // and messaging.
            dispatch("LISTEN_EVENTS", {
              conditions: [
                ["type", "==", "ooo"],
                ["end", ">", new Date()],
              ],
              listenerPath: "events-ooo",
            });
          }
          commit("INIT");
          resolve();
        } else {
          // Logged out
          if (state.initialized) {
            commit("LOG_OUT");
          } else {
            commit("INIT");
          }
          resolve();
        }
      },
      (error) => {
        if (!state.initialized) {
          reject(error);
        }
        dispatch("NOTIFY_ERROR", error);
      }
    );
  });
};

export const LOG_IN = async (context, { email, password }) => {
  let { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
};

export const LOG_OUT = ({ commit }) => {
  commit("LOG_OUT");
  return signOut(auth);
};

export const RESET_PASSWORD = (context, email) => {
  return sendPasswordResetEmail(auth, email);
};

export const VERIFY_EMAIL = (context, oobCode) => {
  return applyActionCode(auth, oobCode);
};

export const VERIFY_PASSWORD_RESET_CODE = (context, oobCode) => {
  return verifyPasswordResetCode(auth, oobCode);
};

export const SET_PASSWORD = (context, { oobCode, password }) => {
  return confirmPasswordReset(auth, oobCode, password);
};

export const BACK_UP = function (context, { path, data }) {
  if (data) {
    LocalStorage.set("backup/" + path, data);
  } else {
    LocalStorage.remove("backup/" + path);
  }
};

export const GET_DOC = async function (
  { commit, state },
  { converter, path, stateKey, stateID, id, forceUpdate }
) {
  if (stateID === undefined && id) {
    stateID = id;
  }
  if (!forceUpdate && stateID in state[stateKey]) {
    return state[stateKey][stateID];
  }
  const snapshot = await getDoc(doc(db, path).withConverter(converter));
  if (!snapshot.exists()) {
    throw "Not found";
  }
  let data = snapshot.data();
  commit("SET", { stateKey, stateID, doc: data });
  return data;
};

export const LISTEN_DOC = async function (
  { commit, dispatch, state },
  { converter, path, stateKey, id, next, error, unlisten, retain }
) {
  if (state.listeners[path]) {
    if (unlisten) {
      await dispatch("UNLISTEN", { path });
    } else {
      let stateID = id;
      if (isFunction(converter.getStateID)) {
        stateID = converter.getStateID(path);
      }
      // See LISTEN_COLLECTION: with callbacks or `retain`, this caller holds
      // a reference; with neither, the listener stays whoever opened it's.
      if (retain || isFunction(next) || isFunction(error)) {
        const payload = { path, next, error };
        commit("LISTEN", payload);
        if (isFunction(next) && state.listeners[path].initialized) {
          next(state[stateKey][stateID]);
        }
        return { path, listenerId: payload.listenerId };
      }
      return false;
    }
  }

  const payload = { path, next, error };
  commit("LISTEN", payload);

  const unsubscribe = onSnapshot(doc(db, path).withConverter(converter), {
    next: (snapshot) => {
      if (!state.listeners[path]) {
        return;
      }
      let doc = null;
      let stateID = snapshot.id;
      if (isFunction(converter.getStateID)) {
        stateID = converter.getStateID(path);
      }
      if (snapshot.exists()) {
        doc = snapshot.data();
        commit("SET", { stateKey, stateID, doc });
      } else {
        commit("REMOVE", { stateKey, stateID });
      }
      commit("MARK_LISTENER_INITIALIZED", { path });
      if (isFunction(state.listeners[path]?.next)) {
        state.listeners[path].next(doc);
      }
    },
    error: (error) => {
      if (state.listeners[path] && isFunction(state.listeners[path].error)) {
        state.listeners[path].error(error);
      }
    },
  });

  if (state.listeners[path]) {
    commit("SET_LISTENER_UNSUBSCRIBE", { path, unsubscribe });
  }
  return { path, listenerId: payload.listenerId };
};

export const GET_COLLECTION = async function (
  { commit },
  {
    converter,
    path,
    colGroup,
    stateKey,
    conditions,
    where: whereClause,
    limitCount,
    limit: limitAlias,
    pagination,
  }
) {
  conditions = conditions ?? whereClause;
  limitCount = limitCount ?? limitAlias;
  // Get the base reference (Collection or CollectionGroup)
  let ref;
  if (colGroup) {
    ref = collectionGroup(db, colGroup);
  } else if (path) {
    ref = collection(db, path);
  } else {
    throw new Error(
      "A 'path' or 'colGroup' must be provided to fetch a collection."
    );
  }
  ref = ref.withConverter(converter);

  // Array to hold all our query constraints (where, orderBy, limit)
  const queryConstraints = [];

  // Apply conditions (where clauses)
  if (conditions && conditions.length) {
    if (isArray(conditions[0])) {
      // If 'conditions' is an array of arrays (e.g., [[field, op, value], ...])
      conditions.forEach((condition) =>
        queryConstraints.push(where(...condition))
      );
    } else {
      // If 'conditions' is a single array (e.g., [field, op, value])
      queryConstraints.push(where(...conditions));
    }
  }

  // Apply limit
  if (limitCount) {
    queryConstraints.push(limit(limitCount));
  }

  // Server-side ordering only changes WHICH documents are returned when a
  // `limit` is applied. Results are stored in an id-keyed map and re-sorted
  // client-side by the table, so ordering an unlimited query is redundant,
  // forces composite indexes, and silently drops docs missing the field.
  if (limitCount && pagination && pagination.sortBy) {
    queryConstraints.push(
      orderBy(pagination.sortBy, pagination.descending ? "desc" : "asc")
    );
    if (pagination.startAt) {
      queryConstraints.push(startAt(pagination.startAt));
    }
    if (pagination.endAt) {
      queryConstraints.push(endAt(pagination.endAt));
    }
  }

  // Construct the final query
  const finalQuery = query(ref, ...queryConstraints);

  // Execute the query
  let snapshot = await getDocs(finalQuery);

  // Map the documents and commit to state.
  const docs = snapshot.docs.map((docSnapshot) => {
    let stateID = docSnapshot.id;
    if (isFunction(converter.getStateID)) {
      stateID = converter.getStateID(docSnapshot.ref.path);
    }
    return { stateID, doc: docSnapshot.data() };
  });

  if (docs.length) {
    commit("SET_MANY", { stateKey, docs });
  }

  return docs.map(({ doc }) => doc);
};

export const LISTEN_COLLECTION = async function (
  { commit, dispatch, state },
  {
    converter,
    path,
    colGroup,
    listenerPath,
    stateKey,
    conditions,
    where: whereClause,
    limitCount,
    limit: limitAlias,
    pagination,
    next,
    error,
    unlisten,
    retain,
  }
) {
  conditions = conditions ?? whereClause;
  limitCount = limitCount ?? limitAlias;
  // Determine the unique path/identifier for this listener
  if (!listenerPath) {
    listenerPath = path || colGroup;
    if (!listenerPath) {
      throw new Error(
        "Cannot determine listenerPath. Provide 'path', 'colGroup', or 'listenerPath'."
      );
    }
  }

  // Check if a listener already exists for this path
  if (state.listeners[listenerPath]) {
    if (unlisten) {
      // If 'unlisten' is true, dispose of the old listener before creating a new one
      await dispatch("UNLISTEN", { path: listenerPath });
    } else {
      // A listener is already up. A caller that brings callbacks -- or asks
      // to `retain` it -- takes a reference on it (LISTEN bumps the refcount)
      // and gets a handle to release with UNLISTEN, so the subscription
      // outlives whichever holder leaves first. With neither, nothing is
      // registered and `false` comes back: the data is in the store, but the
      // listener belongs to whoever opened it and goes away when THEY do.
      if (retain || isFunction(next) || isFunction(error)) {
        const payload = { path: listenerPath, next, error };
        commit("LISTEN", payload);
        // If the listener was already initialized, call the new 'next' callback immediately.
        // The `onSnapshot` function itself calls the `next` callback upon initial subscription.
        if (isFunction(next) && state.listeners[listenerPath].initialized) {
          next(state[stateKey]); // Pass the current data from your state for immediate consistency
        }
        return { path: listenerPath, listenerId: payload.listenerId };
      }
      return false;
    }
  }

  // Get the base reference (Collection or CollectionGroup)
  let ref;
  if (colGroup) {
    ref = collectionGroup(db, colGroup);
  } else if (path) {
    ref = collection(db, path);
  } else {
    throw new Error(
      "Either 'path' or 'colGroup' must be provided for the collection."
    );
  }
  ref = ref.withConverter(converter);

  // Array to hold all our query constraints (where, orderBy, limit)
  const queryConstraints = [];

  // Apply conditions (where clauses)
  if (conditions && conditions.length) {
    if (isArray(conditions[0])) {
      // If 'conditions' is an array of arrays (e.g., [[field, op, value], ...])
      conditions.forEach((conditionArgs) =>
        queryConstraints.push(where(...conditionArgs))
      );
    } else {
      // If 'conditions' is a single array (e.g., [field, op, value])
      queryConstraints.push(where(...conditions));
    }
  }

  // Apply limit
  if (limitCount) {
    queryConstraints.push(limit(limitCount));
  }

  // Server-side ordering only changes WHICH documents are returned when a
  // `limit` is applied. Listener results are stored in an id-keyed map and
  // re-sorted client-side by the table, so ordering an unlimited query is
  // redundant, forces composite indexes, and silently drops docs missing the
  // field (or is outright invalid alongside an inequality `where`).
  if (limitCount && pagination && pagination.sortBy) {
    queryConstraints.push(
      orderBy(pagination.sortBy, pagination.descending ? "desc" : "asc")
    );
    if (pagination.startAt) {
      queryConstraints.push(startAt(pagination.startAt));
    }
    if (pagination.endAt) {
      queryConstraints.push(endAt(pagination.endAt));
    }
  }

  // Construct the final query
  const finalQuery = query(ref, ...queryConstraints);

  const payload = { path: listenerPath, error, next };
  commit("LISTEN", payload);

  // Attach the real-time listener
  const unsubscribe = onSnapshot(finalQuery, {
    next: (snapshot) => {
      // Batch document changes to reduce Vuex commit/reactivity churn
      const toSet = [];
      const toRemove = [];

      snapshot.docChanges().forEach((change) => {
        let stateID = change.doc.id;
        if (isFunction(converter.getStateID)) {
          stateID = converter.getStateID(change.doc.ref.path);
        }
        if (change.type === "removed") {
          toRemove.push(stateID);
        } else {
          // Guard: change.doc.data() can return undefined when the
          // Firestore converter rejects malformed data or when a doc
          // is mid-deletion. Pushing undefined here would have us
          // SET_MANY map[stateID] = deepFreeze(undefined) = undefined,
          // and the next consumer that does `Object.values(...).filter(
          // (e) => e.foo)` crashes with "Cannot read properties of
          // undefined."
          const doc = change.doc.data();
          if (doc) {
            toSet.push({ stateID, doc });
          } else {
            toRemove.push(stateID);
          }
        }
      });

      // Commit batched changes
      if (toSet.length) {
        commit("SET_MANY", { stateKey, docs: toSet });
      }
      if (toRemove.length) {
        commit("REMOVE_MANY", { stateKey, stateIDs: toRemove });
      }

      // Mark listener as initialized and call user's 'next' callback.
      if (state.listeners[listenerPath]) {
        // Ensure listener still exists in state
        commit("MARK_LISTENER_INITIALIZED", { path: listenerPath });
        if (isFunction(state.listeners[listenerPath]?.next)) {
          state.listeners[listenerPath].next(state[stateKey]); // Pass the updated data from your state
        }
      }
    },
    error: (err) => {
      console.error(`Firestore listener error for ${listenerPath}:`, err);
      if (
        state.listeners[listenerPath] &&
        isFunction(state.listeners[listenerPath].error)
      ) {
        state.listeners[listenerPath].error(err);
      }
      dispatch("UNLISTEN", { path: listenerPath });
    },
  });

  // Store the unsubscribe function and callbacks
  if (state.listeners[listenerPath]) {
    commit("SET_LISTENER_UNSUBSCRIBE", { path: listenerPath, unsubscribe });
  }
  return { path: listenerPath, listenerId: payload.listenerId };
};

export const UNLISTEN = function ({ commit, state }, target) {
  const { path, listenerId } =
    typeof target === "string" ? { path: target } : target || {};
  if (!path || !(path in state.listeners)) {
    return;
  }
  const listener = state.listeners[path];
  const unsubscribe = listener.unsubscribe;
  const force = !listenerId;
  const shouldUnsubscribe = force || listener.refCount <= 1;
  commit("UNLISTEN", { path, listenerId, force });
  if (shouldUnsubscribe && isFunction(unsubscribe)) {
    unsubscribe();
  }
};

export const SEND_EMAIL = async function (
  { dispatch, state },
  { to, cc, replyTo, subject, message, attachments }
) {
  // Guard against an empty primary recipient. The Firebase "Trigger Email"
  // extension drops messages whose `to` is empty, which would silently lose
  // any `cc` recipients as well (e.g. accounting CC'd on an auto-invoice when
  // the customer recipient is suppressed via "Do not notify" or is missing an
  // email address). Promote `cc` into `to` so the extension always has a
  // valid primary recipient and the intended audience still receives the mail.
  const isEmptyRecipient = (v) =>
    !v || (isArray(v) && v.length === 0) || v === "";
  if (isEmptyRecipient(to) && !isEmptyRecipient(cc)) {
    to = cc;
    cc = undefined;
  }

  let mailRef;
  let data = {
    createdBy: state.user.id,
    to,
    message: {
      subject,
      html: message,
      text: formatPlainText(message),
    },
  };
  if (cc) {
    data.cc = cc;
  }
  if (replyTo) {
    data.replyTo = replyTo;
  }
  if (attachments) {
    data.message.attachments = attachments;
  }
  mailRef = await addDoc(collection(db, "mail"), data);

  let notification;
  return new Promise(async (resolve, reject) => {
    let timer;
    let settled = false;
    // The queue doc is deleted once delivery finishes; ignore anything the
    // listener reports after that (a torn-down listen can still surface a
    // permission-denied for the now-missing document).
    const cleanUp = () => {
      settled = true;
      unlisten();
      deleteDoc(mailRef).catch(() => {});
    };
    const startTimer = (delay) => {
      timer = setTimeout(() => {
        clearTimeout(timer);
        cleanUp();
        reject("timeout");
      }, delay);
      return delay;
    };

    notification = await dispatch("NOTIFY", {
      icon: "email",
      message: i18n.global.t("success.emailQueued"),
      timeout: startTimer(6e4),
      position: "top-right",
      group: false,
      spinner: true,
      progress: true,
      actions: [],
    });

    const unlisten = onSnapshot(mailRef, {
      next: (snapshot) => {
        const data = snapshot.data();
        if (data && data.delivery) {
          clearTimeout(timer);
          switch (data.delivery.state) {
            case "PENDING":
              break;
            case "PROCESSING":
              {
                const expireTime = timestampToDate(
                  data.delivery.leaseExpireTime
                );
                if (expireTime) {
                  notification({
                    icon: "email_sync",
                    message: i18n.global.t("success.emailProcessing"),
                    timeout: startTimer(expireTime - new Date()),
                  });
                }
              }
              break;
            case "SUCCESS":
              cleanUp();
              resolve(data.delivery);
              break;
            case "ERROR":
              cleanUp();
              reject(data.delivery.error);
              break;
          }
        }
      },
      error: (error) => {
        if (settled) {
          return;
        }
        reject(error);
      },
    });
  })
    .then((result) => {
      if (notification && isFunction(notification)) {
        notification();
      }
      dispatch("NOTIFY_SUCCESS", "emailSent");
      return result;
    })
    .catch((error) => {
      if (notification && isFunction(notification)) {
        notification();
      }
      throw error;
    });
};
